import type { Area } from 'react-easy-crop'

export const createImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    // needed to avoid cross-origin issues
    image.setAttribute('crossOrigin', 'anonymous')
    image.src = url
  })

const getRadianAngle = (degreeValue: number) => (degreeValue * Math.PI) / 180

const rotateSize = ({ height, rotation, width }: { height: number; rotation: number; width: number }) => {
  const rotRad = getRadianAngle(rotation)

  return {
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
  }
}

const getCroppedImg = async ({
  fillStyle = 'white',
  flip = { horizontal: false, vertical: false },
  imageSrc,
  pixelCrop,
  rotation = 0,
}: {
  fillStyle?: string
  flip?: {
    horizontal: boolean
    vertical: boolean
  }
  imageSrc: string
  pixelCrop: Area
  rotation?: number
}): Promise<[null | string, Blob | null]> => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return [null, null]
  }

  // calculate bounding box of the rotated image
  const { height: bBoxHeight, width: bBoxWidth } = rotateSize({ height: image.height, rotation, width: image.width })

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth
  canvas.height = bBoxHeight

  if (rotation || flip.horizontal || flip.vertical) {
    // translate canvas context to a central location to allow rotating and flipping around the center
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
    ctx.rotate(getRadianAngle(rotation))
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1)
    ctx.translate(-image.width / 2, -image.height / 2)
  }

  // draw the image in rotated context
  ctx.drawImage(image, 0, 0)

  // croppedAreaPixels values are bounding box relatively
  // extract the cropped image using these values
  const croppedCanvas = document.createElement('canvas')

  const croppedCtx = croppedCanvas.getContext('2d')

  if (!croppedCtx) {
    return [null, null]
  }

  // Set the size of the canvas to crop from
  croppedCanvas.width = pixelCrop.width
  croppedCanvas.height = pixelCrop.height

  // Draw the cropped image onto the new canvas
  croppedCtx.fillStyle = fillStyle
  croppedCtx.fillRect(0, 0, croppedCanvas.width, croppedCanvas.height)
  croppedCtx.drawImage(
    canvas,
    Math.max(pixelCrop.x, 0),
    Math.max(pixelCrop.y, 0),
    pixelCrop.width,
    pixelCrop.height,
    pixelCrop.x < 0 ? -pixelCrop.x : 0,
    pixelCrop.y < 0 ? -pixelCrop.y : 0,
    pixelCrop.width,
    pixelCrop.height,
  )

  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve) => {
    croppedCanvas.toBlob((file) => resolve([file ? URL.createObjectURL(file) : null, file]), 'image/jpeg')
  })
}
export default getCroppedImg
